import { useCallback, useRef, useState, type ChangeEvent, type MouseEvent } from 'react';
import { useEditorRef } from 'platejs/react';
import { toast } from 'react-toastify';
import { docxFileToFragment } from '../../serializers/docxToPlate';
import { markdownToPlate } from '../../ai/markdownToPlate';
import { sanitizePlateFragment } from '../../serializers/sanitizePlateFragment';
import { processImagesBeforeSave } from '../../services/imageUploadService';
import { classifyImportFile } from './importFormats';
import { applyImportedFragment } from './importFragment';
import { IMPORT_MODE, IMPORT_PHASE, IMPORT_TOASTS, IMPORT_UPLOAD_SOURCE, type ImportMode, type ImportPhase } from './constants';

function yieldToPaint(): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, 0);
    });
}

export function useImportDocxLogic() {
    const editor = useEditorRef();
    const inputRef = useRef<HTMLInputElement>(null);
    const applyingRef = useRef(false);
    const [phase, setPhase] = useState<ImportPhase>(IMPORT_PHASE.IDLE);
    const [pendingFragment, setPendingFragment] = useState<any[] | null>(null);

    const onClick = useCallback((e: MouseEvent) => {
        e.preventDefault();
        inputRef.current?.click();
    }, []);

    const onFile = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (e.target) e.target.value = '';
            if (!file) return;

            const kind = classifyImportFile(file.name);
            // This button's own file input only advertises .md/.doc/.docx —
            // 'html' is a different import surface (Reports' file-list import) —
            // so it's excluded here defensively rather than silently routed to docxFileToFragment.
            if (!kind || kind === 'html') {
                toast.error(IMPORT_TOASTS.BAD_TYPE);
                return;
            }

            // Show the modal immediately — mammoth + sanitize of a CAM-sized DOCX
            // used to run with no UI, so it looked like Import did nothing.
            setPhase(IMPORT_PHASE.READING);
            try {
                let fragment: any[] = [];
                if (kind === 'md') {
                    const text = await file.text();
                    fragment = (markdownToPlate(editor, text) as any[]) ?? [];
                } else {
                    const result = await docxFileToFragment(editor, file);
                    fragment = result.fragment;
                }

                if (!fragment.length) {
                    toast.error(IMPORT_TOASTS.EMPTY);
                    setPhase(IMPORT_PHASE.IDLE);
                    return;
                }

                // Image offload waits until Replace/Append — it is the slow network
                // part and was blocking the mode modal. Autosave still sees docIds
                // because we offload before insert.
                setPendingFragment(sanitizePlateFragment(fragment));
                setPhase(IMPORT_PHASE.CHOOSING);
            } catch (err) {
                console.error('[ImportDocxButton] failed to import document', err);
                setPhase(IMPORT_PHASE.IDLE);
                if (kind === 'doc') {
                    toast.error(IMPORT_TOASTS.LEGACY_DOC);
                } else {
                    toast.error(IMPORT_TOASTS.FAILED);
                }
            }
        },
        [editor],
    );

    const applyFragment = useCallback(
        async (mode: ImportMode) => {
            if (!pendingFragment || applyingRef.current) return;
            applyingRef.current = true;
            setPhase(IMPORT_PHASE.APPLYING);
            // Let the "Adding content…" status paint before the (main-thread) insert.
            await yieldToPaint();
            let ready = pendingFragment;
            try {
                try {
                    ready = (await processImagesBeforeSave(pendingFragment, [], IMPORT_UPLOAD_SOURCE)) as any[];
                } catch (err) {
                    console.error('[ImportDocxButton] image offload failed; inserting with inline images', err);
                }
                applyImportedFragment(editor, ready, mode);
            } catch (err) {
                console.error('[ImportDocxButton] failed to apply imported fragment', err);
                toast.error(IMPORT_TOASTS.FAILED);
            } finally {
                applyingRef.current = false;
                setPendingFragment(null);
                setPhase(IMPORT_PHASE.IDLE);
            }
        },
        [pendingFragment, editor],
    );

    const handleReplace = useCallback(() => {
        void applyFragment(IMPORT_MODE.REPLACE);
    }, [applyFragment]);

    const handleAppend = useCallback(() => {
        void applyFragment(IMPORT_MODE.APPEND);
    }, [applyFragment]);

    const handleCancel = useCallback(() => {
        if (applyingRef.current || phase === IMPORT_PHASE.READING || phase === IMPORT_PHASE.APPLYING) return;
        setPendingFragment(null);
        setPhase(IMPORT_PHASE.IDLE);
    }, [phase]);

    const busy = phase === IMPORT_PHASE.READING || phase === IMPORT_PHASE.APPLYING;

    return {
        inputRef,
        phase,
        busy,
        onClick,
        onFile,
        handleReplace,
        handleAppend,
        handleCancel,
    };
}
